import { api } from "@/lib/api";

const LS_KEY = "suipads_push_topics";

export function pushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function notifPermission() {
  return pushSupported() ? Notification.permission : "unsupported";
}

function urlB64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  return reg;
}

// Returns the active PushSubscription (subscribing if needed). Must be called from a user gesture.
export async function enablePush({ address = null, topics = ["graduation"] } = {}) {
  if (!pushSupported()) throw new Error("Push notifications aren't supported on this device.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");
  const reg = await getRegistration();
  const { data } = await api.get("/push/public-key");
  if (!data?.publicKey) throw new Error("Push is not configured on the server.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(data.publicKey),
    });
  }
  const json = sub.toJSON();
  localStorage.setItem(LS_KEY, JSON.stringify(topics));
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys, address, topics });
  return json.endpoint;
}

export async function getEndpoint() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) return null;
    const sub = await reg.pushManager.getSubscription();
    return sub ? sub.toJSON().endpoint : null;
  } catch (e) { return null; }
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.toJSON().endpoint;
  await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
  localStorage.removeItem(LS_KEY);
}

export function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}
